import React, { useMemo } from "react";
import { parseISO } from "date-fns";
import { useOrgUsersList } from "../../../../../../../../../hooks/useOrgUsersList";
import { useGetTicketCategoriesQuery } from "../../../../../../../../../redux/services/helpdeskApi";
import { getDateTimeByFormat } from "../../../../../../../../../redux/slices/dateTimeFormatSlice";
import { Avatar } from "../../../../../../../../../components/Avatar/Avatar";
import LogsIcon from "../../../../../../../../../assets/svg/logs-icon";
import VerbafloLogoIcon from "../../../../../../../../../assets/images/verbaflo-logo.png";
import type {
  HelpdeskStage,
  HelpdeskTicketActivityLog,
  TicketLogsContentHelpers,
} from "../../../../../utils/types";
import {
  TICKET_LOG_SHIMMER_CLASS,
  TICKET_LOG_SHIMMER_WIDTHS,
} from "../../../../../utils/constant";
import {
  TICKET_LOG_ACTIVITY_CONFIG,
  TICKET_LOG_DEFAULT_ACTIVITY_CONFIG,
  getActorName,
  getFieldKeys,
  getFieldUpdate,
  groupLogsByDate,
} from "../../../../../utils/function";

interface TicketLogsProps {
  activityLogs?: HelpdeskTicketActivityLog[] | null;
  isLoading?: boolean;
  stages?: HelpdeskStage[];
}

const TicketLogs = ({ activityLogs, isLoading, stages }: TicketLogsProps) => {
  const { activeUsers } = useOrgUsersList();
  const { data: ticketCategoriesData } = useGetTicketCategoriesQuery();

  const usersMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of activeUsers) {
      if (user._id && user.name) map.set(user._id, user.name);
    }
    return map;
  }, [activeUsers]);

  const sortedLogs = useMemo(
    () =>
      [...(activityLogs ?? [])].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
    [activityLogs]
  );

  const helpers: TicketLogsContentHelpers = {
    stages,
    usersMap,
    ticketCategories: ticketCategoriesData,
  };

  if (isLoading) return <TimelineShimmer />;

  if (!sortedLogs.length) return <EmptyState />;

  const { today, yesterday, olderGroups } = groupLogsByDate(sortedLogs);
  const olderEntries = Object.entries(olderGroups).sort(([a], [b]) =>
    b.localeCompare(a)
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center h-[50px] px-3 border-b border-border-gray dark:border-border-gray-dk shrink-0">
        <h3 className="text-base font-semibold text-verba-indigo dark:text-verba-indigo-dk">
          Logs
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto">
        <TimelineGroup
          items={today}
          title="Today"
          helpers={helpers}
          isLast={
            today.length > 0 &&
            yesterday.length === 0 &&
            olderEntries.length === 0
          }
        />
        <TimelineGroup
          items={yesterday}
          title="Yesterday"
          helpers={helpers}
          isLast={yesterday.length > 0 && olderEntries.length === 0}
        />
        {olderEntries.map(([dateKey, items], index) => (
          <React.Fragment key={dateKey}>
            <TimelineGroup
              items={items}
              title={getDateTimeByFormat(parseISO(dateKey)).date}
              helpers={helpers}
              isLast={index === olderEntries.length - 1}
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default TicketLogs;

const TimelineGroup = ({
  items,
  title,
  helpers,
  isLast,
}: {
  items: HelpdeskTicketActivityLog[];
  title: string;
  helpers: TicketLogsContentHelpers;
  isLast: boolean;
}) => {
  if (!items.length) return null;

  return (
    <div
      className={`px-3 pb-6 pt-3 flex flex-col gap-3 ${
        !isLast ? "border-b border-border-gray dark:border-border-gray-dk" : ""
      }`}
    >
      <h3 className="text-md font-semibold text-data dark:text-data-dk uppercase leading-normal">
        {title}
      </h3>
      <div className="flex flex-col gap-3">
        {items.map((log, index) => (
          <TimelineItem
            key={`${log.created_at}-${index}`}
            log={log}
            helpers={helpers}
            isLast={index === items.length - 1}
          />
        ))}
      </div>
    </div>
  );
};

const TimelineItem = ({
  log,
  helpers,
  isLast,
}: {
  log: HelpdeskTicketActivityLog;
  helpers: TicketLogsContentHelpers;
  isLast: boolean;
}) => {
  const actorName = getActorName(log);
  const isSystem = actorName === "System";
  const activityType = log.activity_type.toLowerCase();
  const config =
    TICKET_LOG_ACTIVITY_CONFIG[activityType] ??
    TICKET_LOG_DEFAULT_ACTIVITY_CONFIG;
  const fieldUpdate = getFieldUpdate(log, ...getFieldKeys(activityType));
  const localDate = parseISO(log.created_at);
  const { date, time } = getDateTimeByFormat(localDate);

  return (
    <div className="flex gap-[6px] items-start">
      <div className="flex flex-col items-center shrink-0 w-6 self-stretch gap-[3px]">
        {isSystem ? (
          <img
            src={VerbafloLogoIcon}
            alt="VerbaFlo"
            className="w-6 h-6 shrink-0 rounded-full"
          />
        ) : (
          <Avatar
            text={actorName}
            size={24}
            className="text-[9.6px] shrink-0"
          />
        )}
        {!isLast && (
          <div className="flex-1 w-px border-l border-dashed border-border-gray dark:border-border-gray-dk" />
        )}
      </div>

      <div className="flex flex-col gap-1 flex-1 min-w-0 justify-center">
        <div className="flex items-center flex-wrap gap-x-[3px] gap-y-[2px]">
          {config.content(actorName, fieldUpdate, helpers)}
        </div>
        <div className="flex items-center gap-1 min-h-[14px]">
          <p className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk tracking-[-0.2px] whitespace-pre">
            {date}
            {"  \u00B7  "}
            {time}
          </p>
          {config.icon && (
            <>
              <span className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
                {"  \u00B7  "}
              </span>
              {config.icon}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const EmptyState = () => (
  <div className="flex flex-col h-full">
    <div className="flex items-center h-[50px] px-3 border-b border-border-gray dark:border-border-gray-dk shrink-0">
      <h3 className="text-base font-semibold text-verba-indigo dark:text-verba-indigo-dk">
        Logs
      </h3>
    </div>
    <div className="flex items-center justify-center flex-1">
      <div className="flex flex-col items-center justify-center gap-2">
        <div className="border border-gray-25 dark:border-gray-25-dk rounded-full p-5">
          <div className="border border-gray-50 dark:border-gray-50-dk rounded-full p-5">
            <div className="border border-gray-100 dark:border-gray-100-dk rounded-full p-5">
              <LogsIcon className="w-[54px] h-[54px] text-gray-150 dark:text-gray-150-dk" />
            </div>
          </div>
        </div>
        <span className="text-md text-data dark:text-data-dk font-medium">
          Nothing&apos;s been logged yet
        </span>
        <span className="text-md text-gray-placeholder dark:text-gray-placeholder-dk text-center">
          All activities and updates will show up here once you start
          <br />
          engaging.
        </span>
      </div>
    </div>
  </div>
);

const TimelineShimmer = () => (
  <div className="flex flex-col h-full">
    <div className="flex items-center h-[50px] px-3 border-b border-border-gray dark:border-border-gray-dk shrink-0">
      <h3 className="text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk">
        Logs
      </h3>
    </div>
    <div className="flex-1 overflow-y-auto">
      {[0, 1].map((groupIndex) => (
        <div
          key={`shimmer-group-${groupIndex}`}
          className={`px-3 pb-6 pt-3 flex flex-col gap-3 ${
            groupIndex === 0
              ? "border-b border-border-gray dark:border-border-gray-dk"
              : ""
          }`}
        >
          <div className={`h-3 w-[52px] ${TICKET_LOG_SHIMMER_CLASS}`} />
          <div className="flex flex-col gap-3">
            {TICKET_LOG_SHIMMER_WIDTHS?.map(([contentW, timeW], itemIndex) => (
              <div
                key={`shimmer-item-${groupIndex}-${itemIndex}`}
                className="flex gap-[6px] items-start"
              >
                <div className="flex flex-col items-center shrink-0 w-6 self-stretch gap-[3px]">
                  <div
                    className={`w-6 h-6 shrink-0 rounded-full ${TICKET_LOG_SHIMMER_CLASS}`}
                  />
                  {itemIndex < TICKET_LOG_SHIMMER_WIDTHS.length - 1 && (
                    <div className="flex-1 w-px border-l border-dashed border-border-gray dark:border-border-gray-dk" />
                  )}
                </div>
                <div className="flex flex-col gap-1 flex-1 min-w-0 justify-center">
                  <div
                    className={`h-[14px] ${contentW} ${TICKET_LOG_SHIMMER_CLASS}`}
                  />
                  <div
                    className={`h-[11px] ${timeW} ${TICKET_LOG_SHIMMER_CLASS}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);
